import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CANVAS_WIDTH = 1672;
const CANVAS_HEIGHT = 941;
const CENTER_X = CANVAS_WIDTH / 2;
const CENTER_Y = CANVAS_HEIGHT / 2;
const WHEEL_RADIUS = Math.hypot(CANVAS_WIDTH, CANVAS_HEIGHT) * 2;
// Radial sequence keeps its original 11-viewport pace; extra ~3 viewports for the closing overlay + second title
// Radial sequence = 5.5 viewports (timeline 0→1); closing overlay = 1.2 viewports (timeline 1→1.2182)
const PIN_SCROLL_VIEWPORTS = 6.7;
const SCRUB_SMOOTHING_SECONDS = 0.85;
const INITIAL_MASTER = 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/a8e89613-9631-482a-96db-800ab7685863_nova-hero-03-silver-long.webp';

// Index 0 is the initial/final frame (silver long, lavender bg); copper swapped into slot 3
const ASSETS = [
  { id: '03_silver_long', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/a8e89613-9631-482a-96db-800ab7685863_nova-hero-03-silver-long.webp' },
  { id: '02_platinum_bob', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/4aa458a2-046e-47dc-a72e-3e85d7fe98fd_02_platinum_bob.webp' },
  { id: '01_copper', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/a8e89613-9631-482a-96db-800ab7685863_nova-hero-01-copper.webp' },
  { id: '04_rose_braided', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/4aa458a2-046e-47dc-a72e-3e85d7fe98fd_04_rose_braided.webp' },
  { id: '05_burgundy', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/4aa458a2-046e-47dc-a72e-3e85d7fe98fd_05_burgundy.webp' },
  { id: '06_textured_curls', src: 'https://public.readdy.ai/ai/img_res/sandbox/70cd55af4e0fec2bc79e4ad21ea00075/4aa458a2-046e-47dc-a72e-3e85d7fe98fd_06_textured_curls.webp' },
];

function makeWedge(index: number, openness = 1) {
  const sectorAngle = 360 / ASSETS.length;
  const sweep = sectorAngle * Math.max(0, Math.min(1, openness));
  const first = index * sectorAngle - 90 + (sectorAngle - sweep) / 2;
  const last = first + sweep;
  const pointAt = (deg: number) => {
    const r = (deg * Math.PI) / 180;
    return `${CENTER_X + WHEEL_RADIUS * Math.cos(r)} ${CENTER_Y + WHEEL_RADIUS * Math.sin(r)}`;
  };
  return `M ${CENTER_X} ${CENTER_Y} L ${pointAt(first)} A ${WHEEL_RADIUS} ${WHEEL_RADIUS} 0 0 1 ${pointAt(last)} Z`;
}

const WEDGES = ASSETS.map((_, i) => makeWedge(i));
const CLOSED_WEDGES = ASSETS.map((_, i) => makeWedge(i, 0));
const ENTRY_ORDER = [...ASSETS.keys()].slice(1).concat(0);
const EXIT_ORDER = [...ASSETS.keys()].slice(1).reverse().concat(0);
const RADIAL_LINES = ASSETS.map((_, i) => {
  const r = (((i * 360) / ASSETS.length - 90) * Math.PI) / 180;
  const dx = Math.cos(r);
  const dy = Math.sin(r);
  const ex = dx > 0 ? (CANVAS_WIDTH - CENTER_X) / dx : dx < 0 ? -CENTER_X / dx : Infinity;
  const ey = dy > 0 ? (CANVAS_HEIGHT - CENTER_Y) / dy : dy < 0 ? -CENTER_Y / dy : Infinity;
  const length = Math.min(...[ex, ey].filter((d) => d > 0));
  return { d: `M ${CENTER_X + dx * length} ${CENTER_Y + dy * length} L ${CENTER_X} ${CENTER_Y}`, length };
});

export default function Hero() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  const trackRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(q.matches);
    q.addEventListener('change', update);
    return () => q.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    const stage = stageRef.current;
    if (!track || !stage) return undefined;
    const media = gsap.matchMedia();
    media.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
      // CSS smooth scrolling fights ScrollTrigger's instant pin corrections
      const html = document.documentElement;
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      const masks = Array.from(stage.querySelectorAll<SVGPathElement>('.mask-transform'));
      const lines = Array.from(stage.querySelectorAll<SVGPathElement>('.animated-divider-line'));
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: track,
          start: 'top top',
          end: () => `+=${window.innerHeight * PIN_SCROLL_VIEWPORTS}`,
          scrub: SCRUB_SMOOTHING_SECONDS,
          pin: stage,
          pinSpacing: true,
          invalidateOnRefresh: true,
        },
      });
      const title = stage.querySelector('.hero-title');
      const overlay = stage.querySelector('.hero-overlay');
      const outro = stage.querySelectorAll('.hero-outro-item');
      // Main title only visible while static: fades as scrolling starts
      tl.to(title, { opacity: 0, y: -12, duration: 0.03, ease: 'power1.out' }, 0);
      tl.to(lines, { attr: { 'stroke-dashoffset': 0 }, duration: 0.06, ease: 'power2.out' }, 0.02);
      ENTRY_ORDER.forEach((si, step) => {
        tl.to(masks[si], { attr: { d: WEDGES[si] }, duration: 0.05, ease: 'power1.inOut' }, 0.08 + step * 0.05);
      });
      tl.to([...masks, ...lines], { attr: { transform: `rotate(360 ${CENTER_X} ${CENTER_Y})` }, duration: 0.26 }, 0.38);
      EXIT_ORDER.forEach((si, step) => {
        tl.to(masks[si], { attr: { d: CLOSED_WEDGES[si] }, duration: 0.05, ease: 'power1.inOut' }, 0.64 + step * 0.05);
      });
      lines.forEach((line) => {
        tl.to(line, { attr: { 'stroke-dashoffset': Number(line.dataset.lineLength) }, duration: 0.05, ease: 'power2.in' }, 0.94);
      });
      // Closing: overlay + second title, held briefly before the pin releases
      tl.to(overlay, { opacity: 1, duration: 0.0674, ease: 'power1.inOut' }, 1.0);
      tl.fromTo(outro, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.0674, stagger: 0.0202, ease: 'power2.out' }, 1.0418);
      tl.to({ p: 0 }, { p: 1, duration: 0.0092 }, 1.209);
      const raf = requestAnimationFrame(() => ScrollTrigger.refresh());
      return () => {
        cancelAnimationFrame(raf);
        html.style.scrollBehavior = prevBehavior;
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });
    return () => media.revert();
  }, []);

  return (
    <section ref={trackRef} id="top" className="relative md:min-h-[100svh]" aria-label="NOVA Hair Studio radial hairstyle transformation">
      <div ref={stageRef} className="relative w-full aspect-[1672/941] md:aspect-auto md:h-[100svh] overflow-hidden bg-background-50 isolate">
        <svg
          className="absolute inset-0 block w-full h-full"
          viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
          preserveAspectRatio={isMobile ? 'xMidYMid meet' : 'xMidYMid slice'}
          role="img"
          aria-label="Radial hairstyle transformations"
        >
          <defs>
            {ASSETS.map((a, i) => (
              <clipPath id={`nova-clip-${a.id}`} clipPathUnits="userSpaceOnUse" key={a.id}>
                <path className="mask-transform" d={CLOSED_WEDGES[i]} transform={`rotate(0 ${CENTER_X} ${CENTER_Y})`} />
              </clipPath>
            ))}
          </defs>
          <image href={INITIAL_MASTER} x="0" y="0" width={CANVAS_WIDTH} height={CANVAS_HEIGHT} preserveAspectRatio="none" />
          {ASSETS.map((a) => (
            <image
              key={a.id}
              href={a.src}
              x="0"
              y="0"
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              preserveAspectRatio="none"
              clipPath={`url(#nova-clip-${a.id})`}
              decoding="async"
            />
          ))}
          <g aria-hidden="true">
            {RADIAL_LINES.map((l, i) => (
              <path
                key={i}
                className="animated-divider-line"
                data-line-length={l.length}
                d={l.d}
                transform={`rotate(0 ${CENTER_X} ${CENTER_Y})`}
                fill="none"
                stroke="#fff"
                strokeWidth="1.25"
                strokeDasharray={`${l.length} ${l.length}`}
                strokeDashoffset={l.length}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        </svg>
        {/* Title split either side of the profile on wide screens; single line at bottom when narrow */}
        <div className="hero-overlay absolute inset-0 z-20 pointer-events-none opacity-0 bg-foreground-950/55" aria-hidden="true"></div>
        <div className="absolute inset-0 z-30 pointer-events-none hidden md:flex flex-col items-center justify-center text-center px-6 text-background-50">
          <p className="hero-outro-item opacity-0 flex items-center gap-3 font-label text-[11px] tracking-[0.32em] uppercase text-background-50/80"><span className="w-10 h-px bg-background-50/50"></span>Six looks &middot; One studio<span className="w-10 h-px bg-background-50/50"></span></p>
          <h2 className="hero-outro-item opacity-0 mt-6 font-hero-title uppercase tracking-[0.14em] leading-[1.15] text-[40px] lg:text-[3.4vw]">Every head,<br />a new story.</h2>
          <p className="hero-outro-item opacity-0 mt-6 max-w-[440px] text-[15px] leading-relaxed text-background-50/80">Cut, colour and styling shaped around who you are.</p>
          <span className="hero-outro-item opacity-0 mt-10 w-6 h-6 flex items-center justify-center text-background-50/80"><i className="ri-arrow-down-line text-lg"></i></span>
        </div>
        <h1 className="hero-title absolute inset-0 z-10 pointer-events-none flex flex-wrap items-end justify-center content-end pb-4 md:pb-12 px-4 lg:flex-nowrap lg:items-center lg:content-center lg:justify-between lg:pb-0 lg:px-[4vw] text-foreground-950 whitespace-nowrap">
          <span className="hero-fade flex flex-col items-start">
            <span className="hidden lg:flex items-center gap-3 font-label text-[11px] tracking-[0.32em] uppercase text-foreground-700">N&deg;01<span className="w-10 h-px bg-foreground-950/40"></span>Hair Studio</span>
            <span className="font-hero-title uppercase tracking-[0.14em] leading-none text-[18px] sm:text-[28px] md:text-[40px] lg:text-[3.1vw] lg:mt-5">Hair with<span className="lg:hidden">&nbsp;</span></span>
            <span className="hidden lg:block mt-5 w-full h-px bg-foreground-950/30"></span>
          </span>
          <span className="hero-fade flex flex-col items-end" style={{ animationDelay: '0.15s' }}>
            <span className="hidden lg:flex items-center gap-3 font-label text-[11px] tracking-[0.32em] uppercase text-foreground-700"><span className="w-10 h-px bg-foreground-950/40"></span>Cut &middot; Colour &middot; Styling</span>
            <span className="font-hero-title uppercase tracking-[0.14em] leading-none text-[18px] sm:text-[28px] md:text-[40px] lg:text-[3.1vw] lg:mt-5">Character</span>
            <span className="hidden lg:block mt-5 w-full h-px bg-foreground-950/30"></span>
          </span>
        </h1>
      </div>
    </section>
  );
}